// @flow
import { List, Record, Map, fromJS, Set } from "immutable"
// $FlowIssue
import diff from "immutablediff"
import moment from "moment"
import * as R from "ramda"

import { get } from "common/utils/get"
import { CONTINENT_BY_COUNTRY } from "common/consts/continents"
import isEmpty from "common/utils/isEmpty"
import constants from "common/consts/config"
import airlineRestrictions from "../../../data/airlineRestrictions.json" // eslint-disable-line import/no-restricted-paths
import Trip from "./Trip"
import JourneyProvider from "./JourneyProvider"
import type Flight from "./Flight"
import type { PART_TYPE } from "../../booking/scenes/ReservationForm/consts/reservationConsts"
import type { JourneyAmenities } from "../../booking/services/reservation/apis/journeyAmenitiesApi"
import { PART_TYPES } from "../../booking/scenes/ReservationForm/consts/reservationConsts"
import type { Stop } from "./Trip"
import type { SupportedJourneyHiddenTypes } from "common/services/journey/types"

const JourneyRecord = Record({
  bookingToken: null,
  hasHoldBagsAllowed: true, // cannot be used in MMB
  sources: Set(),
  trips: List(),
  transfers: List(),
  flights: List(),
  id: null,
  prices: Map(),
  quality: null,
  debugColor: "",
  debugString: "",
  _original: null, // original response, only for debugging purposes
  type: "oneWay",
  providers: List(),
  disabledBagsInMmb: false,
  hiddenFlightsType: null,
  newOvernight: null, // shot
  countryFrom: "",
  countryTo: "",
})

export const getMidnight = (date: moment) => moment.utc(date).startOf("day")

export type DifferentAirports = Map<number, Object> // eslint-disable-line flowtype/no-weak-types
export type Layover = Map<string, any> // eslint-disable-line flowtype/no-weak-types
export type Layovers = List<Layover>
export type ProvidersListType = List<JourneyProvider>

export default class Journey extends JourneyRecord {
  _flights: ?List<Flight> = null

  _flightsId: ?string = null

  _naturalId: ?string = null

  getJourneyDiff(journey: Journey) {
    return diff(
      this.remove("sources").remove("_original"),
      journey.remove("sources").remove("_original"),
    )
  }

  getTimeUntil(unit: string = "seconds") {
    return this.getDeparture()
      .getIn(["when", "utc"])
      .diff(moment.utc(), unit)
  }

  /**
    Computes id from flight ids
    Should replace static getId in the future
   */
  getId(): string {
    if (!this._flightsId) {
      this._flightsId = this.getFlightsIds().join("-")
    }
    return this._flightsId
  }

  getNaturalId(): string {
    if (!this._naturalId) {
      this._naturalId = this.getFlights()
        .map(flight => flight.naturalId)
        .join("|")
    }
    return this._naturalId
  }

  hasShortLayover(threshold: number = 300000): boolean {
    return !R.compose(
      isEmpty,
      R.filter(duration => duration < threshold), // shorter than 5 minutes
      R.filter(Boolean),
      R.flatten,
      R.map(stop => stop.get("duration")),
    )(this.getStops())
  }

  hasNegativeLayover(): boolean {
    return !R.compose(
      isEmpty,
      R.filter(duration => duration < 0), // negative
      R.filter(Boolean),
      R.flatten,
      R.map(stop => stop.get("duration")),
    )(this.getStops())
  }

  hasHiddenTrip(): boolean {
    return this.get("trips").some(trip => trip.isHidden())
  }

  hasHiddenFlight(): boolean {
    return this.getFlights().some(flight => flight.isHidden)
  }

  hasBus(): boolean {
    return this.getTypes().includes(PART_TYPES.BUS)
  }

  hasTrain(): boolean {
    return this.getTypes().includes(PART_TYPES.TRAIN)
  }

  hasFlight(): boolean {
    return this.getTypes().includes(PART_TYPES.FLIGHT)
  }

  isMultiModal(): boolean {
    return this.hasTrain() || this.hasBus()
  }

  getHiddenFlightsType(): SupportedJourneyHiddenTypes {
    return this.get("hiddenFlightsType")
  }

  getPrioCabinBagsFlights(): Flight[] {
    return R.flatten(this.trips.map(trip => trip.getPrioCabinBagsFlights()).toArray())
  }

  getPriorityBoardingFlights(): Flight[] {
    return R.flatten(this.trips.map(trip => trip.getPriorityBoardingFlights()).toArray())
  }

  getFlightsByAirline(flightCode: string): Flight[] {
    return this.getFlights().filter(flight => flight.getIn(["airline", "code"]) === flightCode)
  }

  /**
   * @deprecated, use native getter instead - for clarity
   */
  getPrice(): number {
    return this.getIn(["prices", "default"])
  }

  getBestPrice(): number {
    if (this.providers.isEmpty()) {
      return this.getPrice()
    }

    return this.providers.minBy(provider => provider.price).price
  }

  getTypes(): Set<PART_TYPE> {
    return this.get("trips").reduce(
      (acc: Set<PART_TYPE>, trip: Trip) => acc.union(trip.getTypes()),
      Set(),
    )
  }

  getOutboundAirlines() {
    return this.getOutboundTrip()
      .flights.toJS()
      .map(flight => flight.operatingAirline.code)
  }

  getInboundAirlines() {
    const inboundTrip = this.getInboundTrip()

    if (!inboundTrip) return []

    return inboundTrip.flights.toJS().map(flight => flight.operatingAirline.code)
  }

  getSourceContinent() {
    const country = this.getIn(["trips", 0, "flights", 0, "departure", "where", "country"])

    return CONTINENT_BY_COUNTRY[country]
  }

  getDestinationContinent() {
    const country = this.getIn(["trips", 0, "flights"])
      .last()
      .getIn(["arrival", "where", "country"])

    return CONTINENT_BY_COUNTRY[country]
  }

  getDeparture() {
    // TODO: Get first trip
    return this.getOutboundTrip().getDeparture()
  }

  getArrival() {
    // TODO: Get last trip
    if (this.isReturn()) {
      return this.getOutboundTrip().getArrival()
    }
    return this.get("trips")
      .last()
      .getArrival()
  }

  fareCategoryTypes(): Array<string> {
    return this.getFlights()
      .map(flight => flight.fareCategory)
      .toArray()
  }

  getItineraryCities() {
    return this.isReturn() ? this.getReturnFlightCities() : this.getMulticityCities()
  }

  getReturnFlightCities(cityId: boolean = false): List<string> {
    return this.get("trips")
      .reduce(
        (cities, trip) =>
          cities.push(
            trip
              .getDeparture()
              .get("where")
              .get(cityId ? "city" : "name"),
          ),
        List(),
      )
      .push(
        this.get("trips")
          .last()
          .getArrival()
          .get("where")
          .get(cityId ? "city" : "name"),
      )
  }

  /**
   * returns list of multicity cities names or ids (all departure and arrival cities - with filtering duplicate entries in a row)
   */
  getMulticityCities(): List<string> {
    const allstops = R.compose(
      R.flatten,
      R.map(pair => [pair.from, pair.to]),
    )(this.getMulticityCityPairs().toArray())
    const [first, ...rest] = allstops
    return R.compose(
      List,
      R.flatten,
      R.concat([first]),
    )(rest.filter((city, index) => city !== allstops[index]))
  }

  /**
   * return list of multicity cities names or ids in pairs { from: departure, to: destination }
   */
  getMulticityCityPairs(cityId: boolean = false) {
    return this.get("trips").map(trip => ({
      from: trip
        .getDeparture()
        .get("where")
        .get(cityId ? "city" : "name"),
      to: trip
        .getArrival()
        .get("where")
        .get(cityId ? "city" : "name"),
    }))
  }

  getMulticityTripsDates() {
    return this.get("trips").map(trip => {
      return {
        departure: trip
          .getDeparture()
          .get("when")
          .get("local")
          .format("YYYY-MM-DD"),
        arrival: trip
          .getArrival()
          .get("when")
          .get("local")
          .format("YYYY-MM-DD"),
      }
    })
  }

  getNumberOfJourneyDays() {
    const latestArrivalTime = this.get("trips")
      .last()
      .get("parts")
      .last()
      .getIn(["arrival", "when", "utc"])

    const firstDepartureTime = this.getDeparture().getIn(["when", "utc"])

    return latestArrivalTime.diff(firstDepartureTime, "days")
  }

  /**
   * @deprecated
   */
  getOutboundTrip() {
    return this.getIn(["trips", 0])
  }

  /**
   * @deprecated
   */
  getInboundTrip() {
    return this.getIn(["trips", 1])
  }

  getFlights(): List<Flight> {
    if (!this._flights) {
      this._flights = this.get("trips").reduce(
        (iflights, trip) => iflights.concat(trip.get("flights")),
        List(),
      )
    }
    return this._flights
  }

  getFlightById(id: string): Flight {
    return this.getFlights().find(({ id: _id }) => id === _id)
  }

  getFlightsIds(): List<*> {
    return this.getFlights().map(x => x.id)
  }

  getConnectingTripsFlights(tripIndex: number) {
    const index = tripIndex || 0
    const outBoundTrip = this.getIn(["trips", index])
    const inboundTrip = this.getIn(["trips", index + 1])

    return {
      arrivalFlight: outBoundTrip.getArrival(),
      departureFlight: inboundTrip && inboundTrip.getDeparture(),
    }
  }

  /**
   * count flight durations + stops durations, do not count stay in destination
   */
  getDuration() {
    return this.get("trips").reduce(
      (duration, trip) => duration.add(trip.getDuration()),
      moment.duration(),
    )
  }

  getFlightsCount() {
    return this.get("trips").reduce((count, trip) => count + trip.getFlightsCount(), 0)
  }

  isOneWay() {
    return this.get("type") === "oneWay"
  }

  isReturn() {
    return this.get("type") === "return"
  }

  isMulticity() {
    return this.get("type") === "multicity"
  }

  endsInFuture() {
    return this.getFlights()
      .last()
      .getIn(["arrival", "when", "utc"])
      .isAfter(moment().utc())
  }

  getEndWhen() {
    return this.getFlights()
      .last()
      .getIn(["arrival", "when"])
  }

  getNumNightsInDestination() {
    const arrival = this.getOutboundTrip()
      .getArrival()
      .getIn(["when", "local"])
    const departure = this.getInboundTrip()
      .getDeparture()
      .getIn(["when", "local"])
    return getMidnight(departure).diff(getMidnight(arrival), "days")
  }

  getLayovers(): Layovers {
    let layovers = List()
    const trips = this.get("trips")
    for (let i = 1; i < trips.count(); i += 1) {
      // Start from second flight
      const prevArrival = trips.get(i - 1).getArrival()
      const currDeparture = trips.get(i).getDeparture()
      const nightsInDestination = getMidnight(currDeparture.getIn(["when", "local"])).diff(
        getMidnight(prevArrival.getIn(["when", "local"])),
        "days",
      )

      const layover: Layover = Map({
        duration: moment.duration(
          currDeparture.getIn(["when", "utc"]).diff(prevArrival.getIn(["when", "utc"])),
        ),
        nightsInDestination,
        place: prevArrival.getIn(["where"]),
        switchToPlace:
          prevArrival.getIn(["where", "code"]) !== currDeparture.getIn(["where", "code"])
            ? currDeparture.getIn(["where"])
            : null,
      })

      layovers = layovers.push(layover)
    }
    return layovers
  }

  getRouteHashLayovers() {
    const trips = this.get("trips")
    if (trips.first().flights.count() === 1) return ""

    return trips
      .map(trip =>
        trip.flights
          .toArray()
          .splice(1)
          .map(flight => flight.departure.getIn(["where", "code"]))
          .join("-"),
      )
      .join(";")
  }

  getFullRouteHashLayovers() {
    const trips = this.get("trips")
    return trips
      .map(trip =>
        [
          ...trip.flights.map(flight => flight.departure.getIn(["where", "code"])).toArray(),
          trip
            .get("flights")
            .last()
            .getIn(["arrival", "where", "code"]),
        ].join("-"),
      )
      .toArray()
      .join(";")
  }

  getJourneyTypesHash() {
    const trips = this.get("trips")
    return trips.map(trip => trip.flights.map(flight => flight.type).join("-")).join(";")
  }

  getCountries() {
    return this.getFlights().reduce((agg, flight) => {
      ;["departure", "arrival"].forEach(x => {
        const country =
          typeof flight.getIn([x, "where", "country"]) !== "undefined"
            ? flight.getIn([x, "where", "country"]).toLowerCase()
            : null
        if (country && !agg.includes(country)) agg = agg.push(country) // eslint-disable-line no-param-reassign
      })
      return agg
    }, List())
  }

  getAirlines(): List<*> {
    return this.getFlights().reduce((agg, flight) => {
      const airline = flight.getIn(["airline", "code"])
      return agg.includes(airline) ? agg : agg.push(airline)
    }, List())
  }

  getAirports(): Set<*> {
    return this.getFlights().reduce(
      (agg, flight) =>
        agg
          .add(flight.getIn(["arrival", "where", "code"]))
          .add(flight.getIn(["departure", "where", "code"])),
      Set(),
    )
  }

  getAirlinesWithNames() {
    return this.getFlights().reduce(
      (acc, flight) =>
        acc.set(flight.getIn(["airline", "code"]), flight.getIn(["airline", "name"])),
      Map(),
    )
  }

  getTripsDifferentAirports() {
    const trips = this.get("trips")
    // Make sense only for journeys with more trips
    return trips.size > 1
      ? trips.reduce((acc, trip, index) => {
          // For first flight, compare with last... for any other, compare with previous
          const tripToCompare = trips.get(index - 1)

          const departure = trip.getDeparture().getIn(["where", "code"])
          const arrival = tripToCompare.getArrival().getIn(["where", "code"])

          return arrival !== departure ? acc.set(index, { arrival, departure }) : acc
        }, Map())
      : Map()
  }

  hasDifferentAirport() {
    return Boolean(this.getFlightsDifferentAirports().size)
  }

  hasLayoverAirportChange() {
    return this.getStops().some(x => x.get("differentAirport"))
  }

  getFlightsDifferentAirports(journeyType: string = this.getType()): DifferentAirports {
    const trips = this.get("trips")

    return trips.reduce((acc, trip, index) => {
      let newAcc = acc
      const flights = trip.get("flights")
      newAcc = newAcc.mergeDeep(
        flights.reduce((flightsAcc, flight, i) => {
          if (i === 0 || flight.get("isHidden")) {
            // Skip first flight
            return flightsAcc
          }

          const compareFlightIndex = i - 1
          const compareFlightArrival = flights.getIn([
            compareFlightIndex,
            "arrival",
            "where",
            "code",
          ])
          const flightDeparture = flight.getIn(["departure", "where", "code"]) // Current flight departure

          return flightDeparture !== compareFlightArrival
            ? flightsAcc.setIn([index, flightDeparture.toLowerCase()], {
                arrival: compareFlightArrival,
                departure: flightDeparture,
                dataIdToHighlight: `${compareFlightIndex}-${compareFlightArrival.toLowerCase()}`,
              })
            : flightsAcc
        }, Map()),
      )

      if (trips.size > 1) {
        const compareTripIndex = index === 0 ? trips.size - 1 : index - 1
        const compareTrip = trips.get(compareTripIndex)
        const departureTrip = trip.getDepartureWithoutHiddenFlights()
        const compareArrivalTrip = compareTrip.getArrivalWithoutHiddenFlights()
        if (!compareArrivalTrip || !departureTrip) {
          return newAcc
        }

        const tripDeparture = departureTrip.getIn(["where", "code"])
        const tripArrival = compareArrivalTrip.getIn(["where", "code"])

        // Some cases highlight departure airport for multicity
        if (index === 0 && journeyType === "multicity") {
          return newAcc
        }

        if (tripDeparture !== tripArrival) {
          const tripIndex = index === 0 ? trips.size - 1 : index
          const airportKey = index === 0 ? tripArrival.toLowerCase() : tripDeparture.toLowerCase()
          const lastPart = index === 0
          newAcc = newAcc.setIn([tripIndex, airportKey], {
            arrival: tripArrival,
            departure: tripDeparture,
            dataIdToHighlight:
              index === 0
                ? `0-${tripDeparture.toLowerCase()}`
                : `${compareTripIndex}-${tripArrival.toLowerCase()}`,
            lastPart,
          })
        }
      }

      return newAcc
    }, Map())
  }

  getType() {
    return this.get("type")
  }

  getStops(): Array<Stop> {
    return R.compose(
      R.flatten,
      R.map(trip => trip.getStops().toArray()),
    )(this.get("trips").toArray())
  }

  getStopovers() {
    const stopovers = []
    const trips = this.get("trips")
    trips.forEach(trip => {
      const flights = trip.get("flights")
      if (flights.size < 2) {
        return stopovers
      }
      flights.forEach(flight => {
        const arrivalWhere = flight.get("arrival").get("where")
        const arrival = arrivalWhere.get("code")
        stopovers.push(arrival)
      })
      return null
    })
    stopovers.pop()
    return stopovers
  }

  // Destination country included
  // Only for `hasMultipleChinaStopover` purpose now
  getStopoversCountriesCounters() {
    const trips = this.get("trips")
    const counter = {}

    trips.forEach(trip => {
      const flights = trip.get("flights")
      flights.forEach(flight => {
        let currCountry = flight
          .get("arrival")
          .get("where")
          .get("country")

        if (currCountry) {
          currCountry = currCountry.toLowerCase()

          if (counter[currCountry]) {
            counter[currCountry] += 1
          } else {
            counter[currCountry] = 1
          }
        }
      })
    })

    return counter
  }

  getTopProvider() {
    const providers = this.providers.sortBy(provider => provider.price)
    return providers.first()
  }

  hasAirline(input: string) {
    return this.get("trips").reduce(
      (result, trip) =>
        result ||
        trip
          .get("flights")
          .reduce(
            (result2, flight) => result2 || flight.get("airline").get("code") === input,
            result,
          ),
      false,
    )
  }

  hasAirport(code: string) {
    return this.getAirports().has(code)
  }

  hasLegacyAirline() {
    return this.getFlights().reduce(
      (agg, flight) =>
        // If there is letter in ID, it is legacy
        agg || /^\D/.test(flight.getId()),
      false,
    )
  }

  hasBagsRecheck() {
    return this.getFlights()
      .toJS()
      .some(get("bagsRecheckRequired"))
  }

  hasGuarantee() {
    return this.getFlights()
      .toJS()
      .some(get("guarantee"))
  }

  hasFreePobedaBag() {
    // https://github.com/facebook/immutable-js/issues/989
    return this.getAirlines().equals(List.of("DP"))
  }

  hasPaidHandBagsAllowed() {
    // https://github.com/facebook/immutable-js/issues/989
    return this.getAirlines().equals(List.of("F9"))
  }

  hasPromocodeAllowed() {
    return !this.getAirlines().some(x => constants.airlinesWithDisallowedPromocode.includes(x))
  }

  hasTransfer() {
    return this.get("transfers").size > 0
  }

  hasAdditionalMealsAllowed() {
    return !this.getAirlines().some(airline => constants.servicesMealsDisallowed.includes(airline))
  }

  hasAvailable(what: string) {
    // Possible `what`s are fields in airlineRestrictions
    // Default is true
    return R.compose(
      R.all(get([what])),
      R.filter(airline => this.hasAirline(airline.id)),
      R.values,
    )(airlineRestrictions)
  }

  // Don't us (handBagsAllowedForInfants, holdBagsAllowedForInfants, bagsAllowedForInfants)
  // all of these functions are incorrect
  // TODO: Get rid off them after BE infants release

  handBagsAllowedForInfants() {
    return this.hasAvailable("holdBagForInfant")
  }

  holdBagsAllowedForInfants() {
    return this.hasAvailable("handBagForInfant")
  }

  hasFlightWithinCountry(country: string): boolean {
    return this.getFlights().some(flight => flight.isFlightWithinCountry(country))
  }

  // TODO: Rather use hand...() and hold...()
  bagsAllowedForInfants() {
    return this.holdBagsAllowedForInfants() && this.handBagsAllowedForInfants()
  }

  isTrainOnly(): boolean {
    return this.getTypes().filter(item => item !== PART_TYPES.TRAIN).size === 0
  }

  isBusOnly(): boolean {
    return this.getTypes().filter(item => item !== PART_TYPES.BUS).size === 0
  }

  isChangeAllowed(hoursAllowedToChange: number) {
    return this.get("trips").every(trip => trip.isChangeAllowed(hoursAllowedToChange))
  }

  isChangeAllowedForAnyTrip(hoursAllowedToChange: number) {
    return this.get("trips").some(trip => trip.isChangeAllowed(hoursAllowedToChange))
  }

  static extendByAmenities(journey: Journey, journeyAmenities: JourneyAmenities): Journey {
    let _journeyAmenities = journeyAmenities

    const trips = journey.get("trips").map(trip => {
      // We have exactly same data in flights and parts for now
      // keep separated for future handling
      const flights = trip
        .get("flights")
        .map((flight, i) => flight.set("amenities", _journeyAmenities[i]))

      const parts = trip
        .get("parts")
        .map((flight, i) => flight.set("amenities", _journeyAmenities[i]))

      // shift used amenities and mutate
      _journeyAmenities = _journeyAmenities.slice(flights.length)
      return trip.set("flights", flights).set("parts", parts)
    })

    return journey.set("trips", trips)
  }

  // eslint-disable-next-line flowtype/no-weak-types
  static fromPlainJS(input: Object) {
    return new Journey({
      ...input,
      sources: Set(input.sources),
      trips: List(input.trips.map(Trip.fromPlainJS)),
      transfers: fromJS(input.transfers),
      prices: fromJS(input.prices),
      providers: List(input.providers.map(JourneyProvider.fromPlainJS)),
    })
  }

  isSameJourney(journey: Journey) {
    return this.bookingToken === journey.bookingToken
  }
}
